/**
 * Context-Aware Async Select Configuration
 * Provides field configuration based on usage context (modal, search, filter)
 * to eliminate confusion about valueField vs searchField usage
 */

import { BaseFieldDefinition, isRelationField } from '../types/fieldRegistry';

/**
 * Context in which async select is being used
 * - modal: Form fields in modals (use ID for value, store in database)
 * - search: Advanced search component (use name for value, query matching)
 * - filter: DataGrid column filters (use name for value, backend filtering)
 */
export type AsyncSelectContext = 'modal' | 'search' | 'filter';

/**
 * Configuration for async select based on context
 */
export interface ContextAwareAsyncSelectConfig {
  apiEndpoint: string;
  searchField: string; // Field to search in (always name field)
  valueField: string; // Field to use as value (context-dependent)
  labelField: string; // Field to display as label (always name field)
  context: AsyncSelectContext;
}

/**
 * Get async select configuration based on usage context
 *
 * **Field Usage Policy:**
 *
 * **Modal Context (Database Storage):**
 * - valueField: ID field (e.g., grade_id) - for database foreign keys
 * - searchField: Name field (e.g., grade_name) - for searching
 * - labelField: Name field (e.g., grade_name) - for display
 * - Use case: Saving to database requires ID values
 *
 * **Search Context (Query Matching):**
 * - valueField: Name field (e.g., grade_name) - for query matching
 * - searchField: Name field (e.g., grade_name) - for searching
 * - labelField: Name field (e.g., grade_name) - for display
 * - Use case: Backend filters by name, not ID
 *
 * **Filter Context (DataGrid Filters):**
 * - valueField: Name field (e.g., grade_name) - for backend filtering
 * - searchField: Name field (e.g., grade_name) - for searching
 * - labelField: Name field (e.g., grade_name) - for display
 * - Use case: Backend filters by name, not ID
 *
 * @param field - Field definition from registry
 * @param context - Usage context (modal, search, or filter)
 * @returns Context-aware configuration or undefined if not applicable
 *
 * @example
 * // Modal field (saves grade_id to database)
 * const modalConfig = getContextAwareAsyncSelectConfig(gradeField, 'modal');
 * // => { valueField: 'grade_id', searchField: 'grade_name', labelField: 'grade_name' }
 *
 * @example
 * // DataGrid filter (filters by grade_name)
 * const filterConfig = getContextAwareAsyncSelectConfig(gradeField, 'filter');
 * // => { valueField: 'grade_name', searchField: 'grade_name', labelField: 'grade_name' }
 */
export function getContextAwareAsyncSelectConfig(
  field: BaseFieldDefinition,
  context: AsyncSelectContext
): ContextAwareAsyncSelectConfig | undefined {
  // Validate field is a relation with API endpoint
  if (!isRelationField(field) || !field.relationConfig.apiEndpoint) {
    return undefined;
  }

  const { relationConfig } = field;
  const apiEndpoint = relationConfig.apiEndpoint;

  // Type guard - we already checked apiEndpoint exists, but TypeScript needs explicit check
  if (!apiEndpoint) {
    return undefined;
  }

  const searchField = relationConfig.searchField || relationConfig.displayField;
  const labelField = relationConfig.displayField;

  // Determine valueField based on context
  let valueField: string;

  switch (context) {
    case 'modal':
      // Modal forms need ID for database storage
      valueField = relationConfig.idField;
      break;

    case 'search':
    case 'filter':
      // Search and filters use name field for backend query matching
      valueField = searchField;
      break;

    default:
      // Should never happen with TypeScript, but safety fallback
      valueField = searchField;
  }

  return {
    apiEndpoint,
    searchField,
    valueField,
    labelField,
    context,
  };
}

/**
 * Get context-aware async select configuration from a field registry
 *
 * @param registry - Array of field definitions
 * @param fieldKey - Key of the field to get config for
 * @param context - Usage context (modal, search, or filter)
 * @returns Context-aware configuration or undefined if not found or not applicable
 *
 * @example
 * import { PRODUCT_FIELDS_REGISTRY } from '../../../domains/products/fields';
 *
 * // Get config for modal field (stores ID)
 * const modalConfig = getContextAwareConfigFromRegistry(
 *   PRODUCT_FIELDS_REGISTRY,
 *   'therapeutic_areas',
 *   'modal'
 * );
 *
 * // Get config for DataGrid filter (filters by name)
 * const filterConfig = getContextAwareConfigFromRegistry(
 *   PRODUCT_FIELDS_REGISTRY,
 *   'therapeutic_areas',
 *   'filter'
 * );
 */
export function getContextAwareConfigFromRegistry(
  registry: BaseFieldDefinition[],
  fieldKey: string,
  context: AsyncSelectContext
): ContextAwareAsyncSelectConfig | undefined {
  const field = registry.find(f => f.key === fieldKey);
  if (!field) {
    return undefined;
  }

  return getContextAwareAsyncSelectConfig(field, context);
}

/**
 * Utility: Convert context-aware config to standard async select config
 * Used when components only need the basic config fields
 */
export function toStandardAsyncSelectConfig(
  contextConfig: ContextAwareAsyncSelectConfig
) {
  return {
    apiEndpoint: contextConfig.apiEndpoint,
    searchField: contextConfig.searchField,
    valueField: contextConfig.valueField,
    labelField: contextConfig.labelField,
  };
}

/**
 * Utility: Check if field should use different value/search fields
 * Useful for detecting when special ID handling is needed
 *
 * @example
 * const needsIdHandling = usesIdFieldAsValue(modalConfig);
 * // => true (modal uses ID, search uses name)
 *
 * const needsIdHandling = usesIdFieldAsValue(filterConfig);
 * // => false (filter uses name for both)
 */
export function usesIdFieldAsValue(
  config: ContextAwareAsyncSelectConfig
): boolean {
  return config.valueField !== config.searchField;
}
